import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "../firebaseConfig";
import DashboardHeader from "./DashboardHeader";
import SetoresChart from "./SetoresChart";
import TreeMapChart from "./TreeMapChart";
import ChamadasTable from "./ChamadasTable";
import RankingDeLike from "./RankingDeLike";
import "bootstrap/dist/css/bootstrap.min.css";

const Dashboard = () => {
  const [sessaoAtiva, setSessaoAtiva] = useState("resumoGeral");

  const [totalChamadas, setTotalChamadas] = useState(0);
  const [totalSolicitacoesConta, setTotalSolicitacoesConta] = useState(0);
  const [tendenciaChamadas, setTendenciaChamadas] = useState([]);
  const [tendenciaContas, setTendenciaContas] = useState([]);
  const [chamadas, setChamadas] = useState([]);
  const [rankingGarcons, setRankingGarcons] = useState([]);
  const [garconsAtivos, setGarconsAtivos] = useState(0);

  // 🔁 Datas de domingo a sábado
  const hoje = new Date();
  const diaSemana = hoje.getDay(); // 0 = domingo
  const domingo = new Date(hoje);
  domingo.setDate(hoje.getDate() - diaSemana);
  const sabado = new Date(domingo);
  sabado.setDate(domingo.getDate() + 6);

  const dataInicioSemana = domingo.toISOString().split("T")[0];
  const dataFimSemana = sabado.toISOString().split("T")[0];

  // 🔍 Buscar chamadas da semana e montar tendências
  useEffect(() => {
    const buscarChamadas = async () => {
      try {
        const q = query(
          collection(db, "chamadas"),
          where("data_hora", ">=", `${dataInicioSemana} 00:00:00`),
          where("data_hora", "<=", `${dataFimSemana} 23:59:59`),
          orderBy("data_hora", "asc")
        );

        const snapshot = await getDocs(q);
        const chamadasSemana = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setChamadas(chamadasSemana);

        // Totais
        setTotalChamadas(chamadasSemana.filter(c => c.tipo === "chamada").length);
        setTotalSolicitacoesConta(chamadasSemana.filter(c => c.tipo === "conta").length);

        // Tendências por dia da semana (índices 0 a 6 → dom a sáb)
        const chamadasPorDia = new Array(7).fill(0);
        const contasPorDia = new Array(7).fill(0);

        chamadasSemana.forEach(chamada => {
          const data = new Date(chamada.data_hora);
          const dia = data.getDay(); // 0 (Dom) a 6 (Sáb)

          if (chamada.tipo === "chamada") chamadasPorDia[dia]++;
          if (chamada.tipo === "conta") contasPorDia[dia]++;
        });

        // Ajustar ordem para labels do gráfico (Seg a Dom)
        const reorder = arr => [arr[1], arr[2], arr[3], arr[4], arr[5], arr[6], arr[0]];
        setTendenciaChamadas(reorder(chamadasPorDia));
        setTendenciaContas(reorder(contasPorDia));

      } catch (error) {
        console.error("Erro ao buscar chamadas:", error);
      }
    };

    buscarChamadas();
  }, [dataInicioSemana, dataFimSemana]);

  // 🔁 Garçons Ativos
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, "garcons"), where("ativo", "==", true)),
      snapshot => setGarconsAtivos(snapshot.size)
    );
    return () => unsubscribe();
  }, []);

  // 🔁 Ranking Garçons
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "rankingGarcons"), snapshot => {
      const rankingData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRankingGarcons(rankingData);
    });
    return () => unsubscribe();
  }, []);

  return (
    <div className="container-fluid">
      <div className="row">
        {/* 🔹 Sidebar */}
        <nav className="col-md-3 col-lg-2 d-md-block bg-dark sidebar">
          <div className="pt-3">
            <h5 className="text-center text-white">📊 Dashboard</h5>
            <ul className="nav flex-column">
              {[
                { id: "resumoGeral", label: "🏠 Resumo Geral" },
                { id: "chamadasPorSetor", label: "📍 Chamadas por Setor" },
                { id: "mapaDeArvore", label: "🌳 Mapa de Árvore" },
                { id: "tabelaChamadas", label: "📋 Detalhes de Chamadas" },
                { id: "rankingGarcons", label: "🏆 Ranking dos Garçons" },
              ].map(({ id, label }) => (
                <li key={id} className="nav-item">
                  <button
                    className={`nav-link btn btn-link text-start w-100 ${sessaoAtiva === id ? "text-primary fw-bold" : "text-white"}`}
                    onClick={() => setSessaoAtiva(id)}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* 🔹 Conteúdo */}
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 pt-3">
          {sessaoAtiva === "resumoGeral" && (
            <>
              <DashboardHeader
                totalChamadas={totalChamadas}
                totalSolicitacoesConta={totalSolicitacoesConta}
                tendenciaChamadas={tendenciaChamadas}
                tendenciaContas={tendenciaContas}
              />
            </>
          )}

          {sessaoAtiva === "chamadasPorSetor" && (
            <>
              <h2 className="text-center mb-4">📍 Chamadas por Setor</h2>
              <div className="card shadow-lg">
                <div className="card-body">
                  <SetoresChart chamadas={chamadas} />
                </div>
              </div>
            </>
          )}

          {sessaoAtiva === "mapaDeArvore" && (
            <>
              <h2 className="text-center mb-4">🌳 Mapa de Árvore</h2>
              <div className="card shadow-lg">
                <div className="card-body">
                  <TreeMapChart chamadas={chamadas} />
                </div>
              </div>
            </>
          )}

          {sessaoAtiva === "tabelaChamadas" && (
            <>
              <h2 className="text-center mb-4">📋 Detalhes de Chamadas</h2>
              <ChamadasTable chamadas={chamadas} />
            </>
          )}

          {sessaoAtiva === "rankingGarcons" && (
            <>
              <h2 className="text-center mb-4">🏆 Ranking dos Garçons</h2>
              <RankingDeLike ranking={rankingGarcons} />
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
